import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { TruckIcon, PackageIcon, MapPinIcon } from "@phosphor-icons/react";

/**
 * SellerShipping — set the cross-listing shipping defaults a seller uses
 * for every drop. Picking carriers here narrows the dropdown shown on
 * each order's "Mark shipped" form, which removes a footgun where a
 * seller picks a carrier they don't actually have a label-print
 * relationship with.
 */
export default function SellerShipping() {
  const { user } = useAuth();
  const [carriers, setCarriers] = useState([]); // registry from backend
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    carriers: [],
    default_free_shipping: false,
    default_flat_fee_dollars: "", // string for input
    default_handling_days: 2,
    ship_from_zip: "",
    ship_from_state: "",
    ship_from_city: "",
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [reg, prof] = await Promise.all([
          api.get("/shipping/carriers"),
          api.get("/me/shipping-profile"),
        ]);
        if (!alive) return;
        setCarriers(reg.data?.items || []);
        const p = prof.data || {};
        setProfile({
          carriers: p.carriers || [],
          default_free_shipping: !!p.default_free_shipping,
          default_flat_fee_dollars:
            p.default_flat_fee_cents != null
              ? (p.default_flat_fee_cents / 100).toFixed(2)
              : "",
          default_handling_days: p.default_handling_days ?? 2,
          ship_from_zip: p.ship_from_zip || "",
          ship_from_state: p.ship_from_state || "",
          ship_from_city: p.ship_from_city || "",
        });
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const toggleCarrier = (slug) => {
    setProfile((p) => {
      const has = p.carriers.includes(slug);
      return {
        ...p,
        carriers: has ? p.carriers.filter((s) => s !== slug) : [...p.carriers, slug],
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const flat = profile.default_flat_fee_dollars.trim();
      const flatCents = flat === "" ? null : Math.round(parseFloat(flat) * 100);
      const payload = {
        carriers: profile.carriers,
        default_free_shipping: profile.default_free_shipping,
        default_flat_fee_cents: profile.default_free_shipping ? null : flatCents,
        default_handling_days: Number(profile.default_handling_days) || 0,
        ship_from_zip: profile.ship_from_zip || null,
        ship_from_state: (profile.ship_from_state || "").toUpperCase().slice(0, 2) || null,
        ship_from_city: profile.ship_from_city || null,
      };
      await api.put("/me/shipping-profile", payload);
      toast.success("Shipping preferences saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white" data-testid="seller-shipping-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <p className="overline text-zinc-500">Seller settings</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1 text-zinc-950">
          Shipping preferences.
        </h1>
        <p className="mt-3 text-zinc-600 max-w-xl leading-relaxed">
          Pick which carriers you ship with and set sensible defaults — these apply
          to every listing unless you override per-listing.
        </p>

        {loading ? (
          <div className="mt-12 text-zinc-500">Loading…</div>
        ) : (
          <div className="mt-10 space-y-10">
            {/* CARRIERS */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <TruckIcon size={20} className="text-[#1D78AB]" />
                <h2 className="font-display font-bold text-xl">Carriers I ship with</h2>
              </div>
              <p className="text-sm text-zinc-600 mb-4">
                Tick every carrier you can drop a package off with. We'll only ever offer
                these in the "Mark shipped" dropdown.
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                {carriers.map((c) => {
                  const checked = profile.carriers.includes(c.slug);
                  return (
                    <label
                      key={c.slug}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${
                        checked
                          ? "border-[#1D78AB] bg-[#1D78AB]/5"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                      data-testid={`carrier-toggle-${c.slug}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCarrier(c.slug)}
                        className="h-4 w-4 accent-[#1D78AB]"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-zinc-900">{c.name}</div>
                        <div className="text-[11px] text-zinc-500 uppercase tracking-wider">
                          {c.domestic && "Domestic"} {c.domestic && c.international && "·"}{" "}
                          {c.international && "International"}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>

            {/* DEFAULT PRICING */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <PackageIcon size={20} className="text-[#1D78AB]" />
                <h2 className="font-display font-bold text-xl">Default shipping cost</h2>
              </div>
              <p className="text-sm text-zinc-600 mb-4">
                Charged at checkout. You can override this per listing.
              </p>
              <label className="flex items-center gap-3 mb-3 cursor-pointer" data-testid="free-shipping-toggle">
                <input
                  type="checkbox"
                  checked={profile.default_free_shipping}
                  onChange={(e) =>
                    setProfile((p) => ({
                      ...p,
                      default_free_shipping: e.target.checked,
                      default_flat_fee_dollars: e.target.checked ? "" : p.default_flat_fee_dollars,
                    }))
                  }
                  className="h-4 w-4 accent-[#1D78AB]"
                />
                <span className="text-sm font-semibold text-zinc-900">
                  Free shipping by default
                </span>
                <span className="text-xs text-zinc-500">
                  (You absorb the cost — great for high-margin items.)
                </span>
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="overline">Default flat fee (USD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="6.95"
                    value={profile.default_flat_fee_dollars}
                    disabled={profile.default_free_shipping}
                    onChange={(e) =>
                      setProfile((p) => ({ ...p, default_flat_fee_dollars: e.target.value }))
                    }
                    className="mt-1"
                    data-testid="default-flat-fee"
                  />
                </div>
                <div>
                  <Label className="overline">Handling days</Label>
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={profile.default_handling_days}
                    onChange={(e) =>
                      setProfile((p) => ({ ...p, default_handling_days: e.target.value }))
                    }
                    className="mt-1"
                    data-testid="default-handling-days"
                  />
                </div>
              </div>
            </section>

            {/* SHIP FROM */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <MapPinIcon size={20} className="text-[#1D78AB]" />
                <h2 className="font-display font-bold text-xl">Ship from</h2>
              </div>
              <p className="text-sm text-zinc-600 mb-4">
                Used to estimate delivery time for buyers. Never shown publicly.
              </p>
              <div className="grid sm:grid-cols-[2fr_1fr_1fr] gap-3">
                <div>
                  <Label className="overline">City</Label>
                  <Input
                    value={profile.ship_from_city}
                    onChange={(e) => setProfile((p) => ({ ...p, ship_from_city: e.target.value }))}
                    className="mt-1"
                    data-testid="ship-from-city"
                    placeholder="Austin"
                  />
                </div>
                <div>
                  <Label className="overline">State</Label>
                  <Input
                    value={profile.ship_from_state}
                    onChange={(e) =>
                      setProfile((p) => ({
                        ...p,
                        ship_from_state: e.target.value.toUpperCase().slice(0, 2),
                      }))
                    }
                    maxLength={2}
                    className="mt-1"
                    data-testid="ship-from-state"
                    placeholder="TX"
                  />
                </div>
                <div>
                  <Label className="overline">ZIP</Label>
                  <Input
                    value={profile.ship_from_zip}
                    onChange={(e) => setProfile((p) => ({ ...p, ship_from_zip: e.target.value }))}
                    className="mt-1"
                    data-testid="ship-from-zip"
                    placeholder="78704"
                  />
                </div>
              </div>
            </section>

            <div className="flex items-center justify-between pt-4 border-t border-zinc-200">
              <Link to="/dashboard" className="text-sm text-zinc-600 hover:underline">
                ← Back to dashboard
              </Link>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-[#1D78AB] hover:bg-[#165d85] text-white h-11 px-7 rounded-full"
                data-testid="save-shipping-profile-btn"
              >
                {saving ? "Saving…" : "Save shipping preferences"}
              </Button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
